import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { SelectDropdown, FormInputError } from "@toddle-edu/ds-web";
import I18nHOC from "UIComponents/I18nHOC";
import Listview from "UIComponents/Listview";
import { Translation } from "react-i18next";
import { getIconComponent } from "Administrator/modules/Utils";
import classes from "./SelectDropdown.scss";
import classNames from "classnames";

//Reference https://dev.to/nioufe/you-should-not-use-lodash-for-memoization-3441
//I did not intentionally use JSON.stringify for cache key. The optons list may be long and will also contain icons component/url. this would be a slow process. I know what I'm doing here!!
const getOptions = ({ options = [], createOptions = [] }) => {
  return _.map([...options, ...createOptions], option => {
    const optionObj = { ...option };
    if (optionObj.icon && optionObj.iconType === "URL") {
      optionObj.icon = (
        <IconImageThumbnail
          iconSize={optionObj.optionIconSize}
          style={optionObj.optionIconStyle}
          icon={optionObj.icon}
        />
      );
    }
    return optionObj;
  });
};

class FormSelectDropdown extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.errorId = _.uniqueId("form-error-");
    this.state = {
      createOptions: [],
      deleteOptionState: {
        isModalOpen: false,
        action: null,
        option: null,
        params: null,
        selectedOption: null,
      },
    };
  }

  updateMultiValue = params => {
    const {
      isUpdateValueWithLabel,
      isUpdateValueWithSelectedOptions,
    } = this.props;
    const options = this.combinedOptions();
    const values = _.map(params, val => val.value);
    if (isUpdateValueWithLabel) this.updateValue(params, options);
    else if (isUpdateValueWithSelectedOptions) this.updateValue(values, params);
    else {
      if (_.isArray(params)) {
        this.updateValue(values, options);
      } else {
        this.updateValue(_.get(params, "value", ""), options);
      }
    }
  };

  //   componentDidCatch = (error, info) => {
  //     console.warn({error, info});
  // }

  getViewValue = () => {
    const options = this.combinedOptions();
    const value = _.get(
      _.find(options, { value: this.props.value }),
      "label",
      ""
    );
    return value;
  };

  onDeleteModalClose = () => {
    this.setState({
      deleteOptionState: { isModalOpen: false },
    });
  };

  onDeleteModalConfirm = () => {
    this.onDeleteModalClose();
    this.updateValueLocally(
      this.state.deleteOptionState.params,
      {
        action: this.state.deleteOptionState.action,
        option: this.state.deleteOptionState.option,
      },
      true
    );
  };

  updateValueLocally = (params, metaData, confirmedFromDeleteModal) => {
    const { action, option } = metaData;
    if (
      this.props.parentEntityMode === "EDIT" &&
      (action === "remove-value" || action === "clear") &&
      this.props.deleteModal.showModalOnOptionDelete &&
      !confirmedFromDeleteModal
    ) {
      const selectValue = this.getSelectValue();
      const currentElement = _.differenceWith(selectValue, params, _.isEqual);

      this.setState(prevState => ({
        deleteOptionState: {
          ...prevState.deleteOptionState,
          action,
          option,
          params,
          isModalOpen: true,
          selectedOption: _.head(currentElement),
        },
      }));

      return;
    }

    if (action === "create-option") {
      const createdOption = {
        ...option,
        id: option.value,
      };
      this.setState(prevState => ({
        createOptions: [...prevState.createOptions, createdOption],
      }));
    }
    if (this.props.multi) {
      this.updateMultiValue(params);
    } else {
      this.updateValue(_.get(params, "value", ""), params);
    }
  };

  combinedOptions = () => {
    const { createOptions } = this.state;
    const { options: optionProps } = this.props;
    const combined = getOptions({ options: optionProps, createOptions });
    //keeps first occurrence
    return _.uniqBy(combined, "value");
  };
  //react-select now wants value as an object to display icons and label in ValueComponent. Thats why we find out value object from Option
  getSelectValue = () => {
    const { value } = this.props;
    let options = this.combinedOptions();

    const isGroupedOptions = !_.isEmpty(_.get(_.head(options), "options", []));
    if (isGroupedOptions) options = _.flatMap(options, "options");

    if (_.isArray(value)) {
      const valuesObj = _.map(value, value =>
        _.find(options, option => option.value === value)
      ).filter(Boolean);
      return valuesObj;
    } else {
      const groupedDropdown = _.some(
        options,
        option => !_.isEmpty(option?.options)
      );

      if (groupedDropdown) {
        const groupedOptions = _.reduce(
          options,
          (result = [], option) => {
            result = _.concat(result, _.get(option, "options", []));

            return result;
          },
          []
        );

        const valueObj = _.find(
          groupedOptions,
          option => option.value === value
        );

        return valueObj ?? null;
      } else {
        const valueObj = _.find(options, option => option.value === value);
        return valueObj ?? null;
      }
    }
  };

  getDisplayValue = () => {
    const selectValue = this.getSelectValue();

    const normalizeOption = option =>
      !_.isNil(_.get(option, "name"))
        ? { ...option, label: _.get(option, "name") }
        : option;

    return _.isArray(selectValue)
      ? _.map(selectValue, normalizeOption)
      : normalizeOption(selectValue);
  };

  renderEdit = () => {
    const {
      allowAddOption: isCreatable = false,
      placeholder,
      clearable = false,
      disabled = false,
      dsVersion = "1.0",
      multi,
      addButtonText = "Add",
      isSearchable = true,
      size = "x-large",
      areOptionsEmptyAllowed,
      noOptionsText = (
        <Translation>{t => t("common:noResultHeading")}</Translation>
      ),
      menuPlacement,
      customErrorClass,
      hideDropdownIndicator,
      showWarning,
      isSelectAllEnabled,
      showOverflowingTagsCount,
      useDynamicOverlayWidth,
      showError = true,
      tooltipPlacement = "bottomLeft",
      testId,
      showCheckbox = false,
      staticOption = null,
      tagType = "filled",
      variant = "outlined",
      noOptionsVariant,
      maxMenuHeight,
      isVirtualized,
    } = this.props;
    const { error = "" } = this.state;

    const localTestId = `${testId}-formSelectDropdown`;

    const options = this.combinedOptions();
    const isDisabled = areOptionsEmptyAllowed
      ? disabled
      : options.length == 0 || disabled;

    const containerClass = classNames("w-full", classes.containerClass);
    const errorClass = classNames({
      [classes.errorClass]: true,
      [customErrorClass]: !_.isEmpty(customErrorClass),
    });

    return (
      <div className={containerClass}>
        <SelectDropdown
          value={this.getDisplayValue()}
          size={size}
          onChange={this.updateValueLocally}
          isClearable={clearable}
          isCreatable={isCreatable}
          isSearchable={isSearchable}
          createText={addButtonText}
          isMulti={multi}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          options={options}
          dsVersion={dsVersion}
          placeholder={placeholder}
          aria-label={placeholder}
          isDisabled={isDisabled}
          error={error}
          noOptionsText={noOptionsText}
          ref={this.updateInputRef}
          menuPlacement={menuPlacement}
          hideDropdownIndicator={hideDropdownIndicator}
          showWarning={showWarning}
          isSelectAllEnabled={isSelectAllEnabled}
          showOverflowingTagsCount={showOverflowingTagsCount}
          useDynamicOverlayWidth={useDynamicOverlayWidth}
          tooltipPlacement={tooltipPlacement}
          testId={localTestId}
          showCheckbox={showCheckbox}
          staticOption={staticOption}
          tagType={tagType}
          variant={variant}
          noOptionsVariant={noOptionsVariant}
          maxMenuHeight={maxMenuHeight}
          aria-describedby={this.errorId}
          isVirtualized={isVirtualized}
        />
        <FormInputError
          id={this.errorId}
          dsVersion={dsVersion}
          {...(showError &&
            !_.isEmpty(error) && { className: errorClass, error })}
        />
        <this.props.deleteModal.DeleteSelectOptionModal
          isOpen={this.state.deleteOptionState.isModalOpen}
          onModalClose={this.onDeleteModalClose}
          onModalConfirm={this.onDeleteModalConfirm}
          deleteOptionState={this.state.deleteOptionState}
          t={this.props.t}
          testId={localTestId}
        />
      </div>
    );
  };

  renderView = () => {
    const { value, customTextStyle, mode } = this.props;
    return (
      <Listview value={value} customTextStyle={customTextStyle} mode={mode} />
    );
  };
}

const IconImageThumbnail = ({ icon, iconSize = 24, style = {} }) => {
  const iconComponent = getIconComponent({
    icon,
    size: "x-small",
    containerStyle: { size: iconSize, ...style },
  });
  return iconComponent;
};

FormSelectDropdown.defaultProps = {
  ...UIBaseComponent.defaultProps,
  areOptionsEmptyAllowed: true,
  options: [],
  multi: false,
  allowAddOption: false,
  isUpdateValueWithLabel: false,
  isUpdateValueWithSelectedOptions: false,
  error: "Required",
  hideDropdownIndicator: false,
  showWarning: false,
  isSelectAllEnabled: true,
  showOverflowingTagsCount: false,
  useDynamicOverlayWidth: false,
  deleteModal: {
    showModalOnOptionDelete: false,
    DeleteSelectOptionModal: () => null,
  },
  dsVersion: "1.0",
  staticOption: null,
  variant: "outlined",
  noOptionsVariant: "default",
  isVirtualized: false,
};

FormSelectDropdown.propTypes = {
  ...UIBaseComponent.propTypes,
  multi: PropTypes.bool,
  clearable: PropTypes.bool,
  options: PropTypes.array,
  areOptionsEmptyAllowed: PropTypes.bool,
  allowAddOption: PropTypes.bool,
  addButtonText: PropTypes.string,
  isUpdateValueWithLabel: PropTypes.bool,
  isUpdateValueWithSelectedOptions: PropTypes.bool,
  menuPlacement: PropTypes.string,
  customErrorClass: PropTypes.string,
  hideDropdownIndicator: PropTypes.bool,
  showWarning: PropTypes.bool,
  isSelectAllEnabled: PropTypes.bool,
  showOverflowingTagsCount: PropTypes.bool,
  useDynamicOverlayWidth: PropTypes.bool,
  deleteModal: PropTypes.object,
  dsVersion: PropTypes.string,
  variant: PropTypes.string,
  noOptionsVariant: PropTypes.string,
  isVirtualized: PropTypes.bool,
};

export default I18nHOC({})(FormSelectDropdown);
